'use client'

import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'
import { cn } from '../utils/cn'

const CarouselContext = React.createContext(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback(api => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      event => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext],
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className={cn('overflow-hidden', className)}>
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-5',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full relative z-10',
        orientation === 'horizontal' ? 'pl-4 flex justify-center' : 'pt-4',
        className,
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel()

    return (
      <button
        ref={ref}
        className={cn(
          'absolute size-12 rounded-full',
          orientation === 'horizontal'
            ? '-left-12 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <ChevronLeft className="size-8" />
        <span className="sr-only">Previous slide</span>
      </button>
    )
  },
)
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel()

    return (
      <button
        size={size}
        className={cn(
          'absolute size-12 rounded-full',
          orientation === 'horizontal'
            ? '-right-12 top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <ChevronRight className="size-8" />
        <span className="sr-only">Next slide</span>
      </button>
    )
  },
)
CarouselNext.displayName = 'CarouselNext'

const CarouselButtons = React.forwardRef(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const {
      orientation,
      scrollNext,
      canScrollNext,
      canScrollPrev,
      scrollPrev,
    } = useCarousel()

    return (
      <div className="flex items-center justify-end my-4 gap-6 relative z-10">
        <button
          ref={ref}
          className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!canScrollPrev}
          onClick={scrollPrev}
          {...props}
        >
          <ChevronLeft className="size-8" />
          <span className="sr-only">Previous slide</span>
        </button>
        <button
          size={size}
          className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!canScrollNext}
          onClick={scrollNext}
          {...props}
        >
          <ChevronRight className="size-8" />
          <span className="sr-only">Next slide</span>
        </button>
      </div>
    )
  },
)
CarouselButtons.displayName = 'CarouselButtons'

const CarouselButtonsMobile = React.forwardRef(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { scrollNext, canScrollNext, canScrollPrev, scrollPrev, api } =
      useCarousel()

    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const [slideCount, setSlideCount] = React.useState(0)

    // Atualiza o índice atual e a contagem de slides quando a API muda
    React.useEffect(() => {
      if (!api) return

      // Obtém o número total de slides
      setSlideCount(api.slideNodes().length)

      // Atualiza o índice selecionado quando o slide muda
      const onSelect = () => {
        setSelectedIndex(api.selectedScrollSnap())
      }

      api.on('select', onSelect)
      // Inicializar o índice
      onSelect()

      return () => {
        api.off('select', onSelect)
      }
    }, [api])

    // Função para navegar diretamente para um slide específico
    const scrollTo = index => {
      api?.scrollTo(index)
    }

    return (
      <div className="flex items-center justify-center my-4 gap-6 relative z-10">
        <button
          ref={ref}
          className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!canScrollPrev}
          onClick={scrollPrev}
          {...props}
        >
          <ChevronLeft className="size-8" />
          <span className="sr-only">Previous slide</span>
        </button>

        {/* Indicators - Gerados dinamicamente */}
        <div className="h-1.5 flex items-center justify-center gap-1.5">
          {Array.from({ length: slideCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollTo(index)}
              className={cn(
                'h-full bg-white rounded-full transition-all',
                selectedIndex === index
                  ? 'w-6 bg-black border border-white'
                  : index === selectedIndex - 1 || index === selectedIndex + 1
                    ? 'w-3.5'
                    : 'w-1.5',
              )}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={selectedIndex === index ? 'true' : 'false'}
            />
          ))}
        </div>

        <button
          size={size}
          className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!canScrollNext}
          onClick={scrollNext}
          {...props}
        >
          <ChevronRight className="size-8" />
          <span className="sr-only">Next slide</span>
        </button>
      </div>
    )
  },
)
CarouselButtonsMobile.displayName = 'CarouselButtonsMobile'

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselButtons,
  CarouselButtonsMobile,
}
